import { useEffect } from 'react';

/**
 * Keeps the <link rel="manifest"> in sync with the tenant slug in the URL.
 *
 * - Root route (/, /auth, /admin, ...) → platform manifest (/manifest.json).
 * - Tenant route (/:slug/app, /:slug/auth, etc.) → tenant-branded manifest
 *   served by the backend at /api/tenants/public/:slug/manifest.json, so
 *   the installed home-screen icon shows THAT tenant's name + logo.
 *
 * Re-runs on every path change (listens to popstate + pushstate hooks).
 * Returns null — purely a side-effect component.
 */

// Routes that are NEVER tenant-scoped, even though their first segment
// looks slug-ish. Keep in sync with App.js's top-level routes.
const RESERVED_ROOTS = new Set([
  'auth', 'admin', 'app', 'api', 'icons', 'static',
  'referral-staff', 'deposit-return', 'portal',
]);
const PLATFORM_DOMAIN = (process.env.REACT_APP_PLATFORM_DOMAIN || 'vegaslobby.vip').toLowerCase();

function extractHostSlug() {
  const host = (window.location.hostname || '').toLowerCase().replace(/^www\./, '');
  const suffix = `.${PLATFORM_DOMAIN}`;
  if (host.endsWith(suffix)) {
    const slug = host.slice(0, -suffix.length);
    return slug && !RESERVED_ROOTS.has(slug) ? slug : null;
  }
  return null;
}

function extractSlug(pathname) {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return null;
  const first = parts[0].toLowerCase();
  if (RESERVED_ROOTS.has(first)) return null;
  return first;
}

function setManifestHref(href) {
  let link = document.querySelector('link[rel="manifest"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    document.head.appendChild(link);
  }
  if (link.getAttribute('href') !== href) {
    link.setAttribute('href', href);
  }
}

export default function TenantManifestLink() {
  useEffect(() => {
    const apply = () => {
      const slug = extractHostSlug() || extractSlug(window.location.pathname);
      if (slug) {
        setManifestHref(`/api/tenants/public/${encodeURIComponent(slug)}/manifest.json`);
      } else {
        setManifestHref('/manifest.json');
      }
    };
    apply();
    // react-router mutates history without firing popstate, so wrap pushState
    // to detect SPA navigations too.
    const wrap = (method) => {
      const orig = history[method];
      history[method] = function (...args) {
        const r = orig.apply(this, args);
        apply();
        return r;
      };
    };
    wrap('pushState');
    wrap('replaceState');
    window.addEventListener('popstate', apply);
    return () => {
      window.removeEventListener('popstate', apply);
    };
  }, []);

  return null;
}
